import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

type CacheType = any;
type AddCacheTypeParams = {key: string, value: any, expires?: Number | Date | null};
type AddCacheType = ({ key, value, expires }: AddCacheTypeParams) => void;
type RemoveCacheType = (key: string) => void;
type GetCacheType = (key: string) => any | undefined;
type IsCacheType = (key: string) => boolean;

interface CacheContextType {
  cache: Record<string, any>;
  addCache: AddCacheType;
  getCache: GetCacheType;
  removeCache: RemoveCacheType;
  isCached: IsCacheType;
}

const CacheContext = createContext<CacheContextType | undefined>(undefined);

export const useCache = () => {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error("useCache must be used within a CacheProvider");
  }
  return context;
};

export const CacheProvider = ({ children }: {children: ReactNode}) => {
    const [cache, setCache] = useState<CacheType>({});

    useEffect(() => {
      let keys: string | null = null;
      try {
        keys = localStorage.getItem("cache:keys");
      } catch (e) {
        console.error("Error reading cache keys from localStorage:", e);
      }
      let cacheKeys = [];
      try {
      cacheKeys = keys ? JSON.parse(keys) : [];
      } catch (e) {
        console.error("Error parsing cache keys from localStorage:", e);
      }
      Object.values(cacheKeys).forEach((key) => {
        const cachedItem = localStorage.getItem("cache:" + key);
        if (cachedItem) {
          const parsedItem = JSON.parse(cachedItem);
          if (parsedItem && parsedItem.value) {
            setCache((prevCache: any) => ({
              ...prevCache,
              [String(key)]: {
                value: parsedItem.value,
                expires: parsedItem.expires || null
              }
            }));
          }
        }
      });
    }, []);

    const isCached: IsCacheType = (key: string): boolean => {
      const item = cache[key];
      if (!item) return false;
      if (item.expires && new Date(item.expires) < new Date()) {
        delete cache[key];
        return false;
      }
      return true;
    }

    const addCache: AddCacheType = ({ key, value, expires = -1 }: AddCacheTypeParams) => {
      if (!key || !value) {
        console.error("Key and value are required for caching");
        return;
      }
      if (expires && expires > -1 && !(expires instanceof Date)) {
        expires = new Date(Date.now() + Number(expires));
      } else if (expires === -1) {
        expires = null; // No expiration
      }
      const newCache = { ...cache, [key]: { value, expires: expires } };
      setCache(newCache);
      try {
        localStorage.setItem("cache:" + key, JSON.stringify(newCache[key]));
      } catch (e) {
        console.error("Error saving cache keys to localStorage:", e);
      }
      const keys = localStorage.getItem("cache:keys");
      const cacheKeys = keys ? JSON.parse(keys) : [];
      if (!cacheKeys.includes(key)) {
        cacheKeys.push(key);
        try {
          localStorage.setItem("cache:keys", JSON.stringify(cacheKeys));
        } catch (e) {
          console.error("Error saving cache keys to localStorage:", e);
        }
      }
    }

    const getCache: GetCacheType = (key: string): any | undefined => {
      const item = cache[key];
      if (!item) return undefined;
      if (item.expires && new Date(item.expires) < new Date()) {
        delete cache[key];
        try {
          localStorage.removeItem("cache:" + key);
        } catch (e) {
          console.error("Error removing cache item from localStorage:", e);
        }
        return undefined;
      }
      return item.value;
    }

    const removeCache = (key: string) => {
      const newCache = { ...cache };
      delete newCache[key];
      setCache(newCache);
      try {
        localStorage.removeItem("cache:" + key);
      } catch (e) {
        console.error("Error removing cache item from localStorage:", e);
      }
      const keys = localStorage.getItem("cache:keys");
      if (keys) {
        const cacheKeys = JSON.parse(keys).filter((k: string) => k !== key);
        try {
          localStorage.setItem("cache:keys", JSON.stringify(cacheKeys));
        } catch (e) {
          console.error("Error saving cache keys to localStorage:", e);
        }
      }
    }

    return (
      <CacheContext.Provider value={{ cache, getCache, addCache, removeCache, isCached }}>
        {children}
      </CacheContext.Provider>
    );
  }
;
